import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import Sidebar from "../../Components/Sidebar/Sidebar";
import LabelHeader from "../../Components/Commons/LabelHeader";
import TextField from "../../Components/Commons/Forms/TextField";
import BtnSave from "../../Components/Commons/Buttons/BtnSave";
import { Radio } from "antd";
import { toast } from "react-toastify";
import { checkPhilhealthIsRegistered, checkPxIsPhilClinicRegistered, getPhilhealthRegistrationByPin /*, checkPhilhealthATC */ } from "../../Components/Helpers/API/PhilhealthApi/PhilhealthApi"; // checkPhilhealthATC commented out - ATC not needed per client
import { fetchPatientSearch } from "../../Components/Helpers/API/PatientApi/PatientApi";

function IsRegisteredCheck() {
  const navigate = useNavigate();
  const location = useLocation();
  const [phicPin, setPhicPin] = useState("");
  const [membership, setMembership] = useState("member");
  const [loading, setLoading] = useState(false);
  // const [atc, setAtc] = useState(""); // Commented out - ATC not needed per client

  // Helper function to parse registration response
  const parseRegistrationResponse = (res) => {
    let registered;
    const d = res?.data;
    let candidate;
    if (Array.isArray(d)) {
      const item = d[0] || {};
      candidate =
        item?.isMemberDependentRegistered ??
        item?.is_patient ??
        item?.is_registered ??
        item?.status ??
        item?.message ??
        item?.result;
    } else {
      candidate =
        (typeof d === "string" ? d : undefined) ??
        d?.isMemberDependentRegistered ??
        d?.is_patient ??
        d?.data?.isMemberDependentRegistered ??
        d?.data?.is_registered ??
        d?.is_registered ??
        d?.data?.status ??
        d?.status ??
        d?.data?.message ??
        d?.message ??
        d?.result;
    }
    if (typeof candidate === "string") {
      const v = candidate.toUpperCase();
      registered = v === "YES" || v.includes("YES") ? true : v === "NO" || v.includes("NO") ? false : undefined;
    } else if (typeof candidate === "boolean") {
      registered = candidate;
    } else if (typeof candidate === "number") {
      registered = candidate === 1;
    }
    return registered;
  };

  const normalizeSex = (value) => {
    const v = String(value || "").trim().toLowerCase();
    if (v === "m" || v === "male") return "male";
    if (v === "f" || v === "female") return "female";
    return v;
  };

  // Helper function to extract patient details from PhilHealth or clinic response for autofill
  const extractPatientDetails = (res, isPhilHealthResponse = false) => {
    console.log('Full response object:', res);
    console.log('res.status:', res?.status);
    const d = res?.data;
    console.log('Raw response data (res.data):', d);
    console.log('d.data:', d?.data);
    console.log('d.patient_data:', d?.patient_data);
    const data = Array.isArray(d) ? d[0] || {} : d?.data || d || {};
    const patientData = d?.patient_data || d?.data?.patient_data || {};
    console.log('Final extracted patient data:', data);
    console.log('Final extracted patient_data:', patientData);
    
    const packageTypeMap = {
      "K": "Konsulta Package",
      "P": "PCB1",
      "E": "Expanded PCB",
    };
    const enlistmentStatusMap = {
      "1": "active",
      "2": "cancelled", 
      "3": "transferred",
    };

    const rawEnlistmentStatus = data?.phic_enlistment_status || data?.enlistment_status || "";
    const mappedEnlistmentStatus = enlistmentStatusMap[String(rawEnlistmentStatus)] || rawEnlistmentStatus || "active";
    const rawPackageType = data?.phic_package_type || data?.package_type || "";
    const mappedPackageType = packageTypeMap[rawPackageType] || rawPackageType || "Konsulta Package";
    // PhilHealth API may return fields with phic_ prefix or without
    const isDependent = membership === "dependent";
    return {
      // Patient ID (for existing patients)
      patient_id: d?.patient_id || patientData.id || data?.id || data?.patient_id || null,
      master_patient_id: patientData.master_id || patientData.master_patient_id || data?.master_patient_id || data?.patient_master_id || null,
      is_fpe: data?.is_fpe || patientData.is_fpe || false,
      
      // Basic patient information — prefer patient_data for demographics
      last_name: patientData.last_name || data?.last_name || data?.lastName || data?.phic_last_name || "",
      first_name: patientData.first_name || data?.first_name || data?.firstName || data?.phic_first_name || "",
      middle_name: patientData.middle_name || data?.middle_name || data?.middleName || data?.phic_middle_name || "",
      suffix: patientData.suffix || data?.suffix || data?.extension_name || data?.phic_extension_name || "",
      birthdate: patientData.birthdate || data?.birthdate || data?.birth_date || data?.phic_birthdate || "",
      contact_no: patientData.contact_no || data?.contact_no || data?.mobile_number || data?.phic_contact_no || "",
      email: patientData.email || data?.email || "",
      sex: normalizeSex(patientData.sex || patientData.gender || data?.sex || data?.gender || data?.primary_sex),
      
      // Address fields from patient_data
      country: patientData.country || data?.country || "PHILIPPINES",
      province: patientData.province || data?.province || "",
      city: patientData.city || data?.city || "",
      barangay: patientData.barangay || data?.barangay || "",
      street: patientData.street || data?.street || "",
      zip_code: patientData.zip_code || data?.zip_code || "",
      
      // Civil status and other demographics
      civil_status: (patientData.civil_status || data?.civil_status || data?.status || "").toLowerCase(),
      occupation: patientData.occupation || data?.occupation || "",
      nationality: patientData.nationality || data?.nationality || "FILIPINO",
      religion: patientData.religion || data?.religion || "",
      
      // Emergency contact from patient_data
      emergency_contact: patientData.emergency_contact || data?.emergency_contact || "",
      emergency_contact_no: patientData.emergency_contact_no || data?.emergency_contact_no || "",
      relation_w_contact: patientData.relation_w_contact || data?.relation_w_contact || "",
      
      // Senior/PWD IDs
      senior_id: patientData.senior_id || data?.senior_id || "",
      pwd_id: patientData.pwd_id || data?.pwd_id || "",
      is_pwd: patientData.pwd_id || data?.pwd_id || patientData.is_pwd === "1" || data?.is_pwd === "1" || data?.is_pwd === 1 ? "1" : "0",
      
      // PhilHealth fields from data (phic_* / primary_*)
      // If membership type is member, autofill phic_pin; if dependent, leave phic_pin empty (member PIN should be entered manually)
      // phic_pin: membership === "member" ? phicPin : (data?.phic_pin || ""),
      phic_pin: data?.primary_pin || '',
      phic_membership_type: membership,
      phic_enlistment_date: data?.phic_enlistment_date || data?.enlistment_date || "",
      // phic_enlistment_status: data?.enlistment_status || data?.phic_enlistment_status || "active",
      phic_enlistment_status: mappedEnlistmentStatus,
      // phic_package_type: data?.package_type || data?.phic_package_type || "Konsulta Package",
      phic_package_type: mappedPackageType,
      phic_atc: data?.atc || data?.phic_atc || "WALKEDIN", // atc state removed - default to WALKEDIN
      
      // PhilHealth member/dependent details
      // If membership type is dependent, autofill phic_dependent_pin with the entered phicPin
      phic_dependent_pin: data?.phic_dependent_pin || (membership === "dependent" ? phicPin : ""),
      phic_last_name: isDependent ? data?.primary_last_name : data?.phic_last_name || data?.last_name || "",
      phic_first_name: isDependent ? data?.primary_first_name : data?.phic_first_name || data?.first_name || "",
      phic_middle_name: isDependent ? data?.primary_middle_name : data?.phic_middle_name || data?.middle_name || "",
      phic_extension_name: isDependent ? data?.primary_suffix : data?.phic_extension_name || data?.suffix || "",
      phic_birthdate: isDependent ? data?.primary_birthdate : data?.phic_birthdate || data?.birthdate || "",
      phic_contact_no: data?.phic_contact_no || patientData.contact_no || data?.contact_no || data?.mobile_number || "",

      // Primary Member Details
      primary_pin: data?.primary_pin || "",
      primary_last_name: data?.primary_last_name || "",
      primary_first_name: data?.primary_first_name || "",
      primary_middle_name: data?.primary_middle_name || "",
      primary_suffix: data?.primary_suffix || "",
      primary_birthdate: data?.primary_birthdate || "",
      primary_sex: data?.primary_sex || "",
    };
  };

  // const handleSearch = async () => {
  //   setLoading(true);
  //   // Only set phic_pin in localStorage for members; for dependents, set phic_dependent_pin instead
  //   if (membership === "member") {
  //     localStorage.setItem("phic_pin", phicPin);
  //     localStorage.removeItem("phic_dependent_pin");
  //   } else {
  //     localStorage.setItem("phic_dependent_pin", phicPin);
  //     localStorage.removeItem("phic_pin");
  //   }
  //   localStorage.setItem("phic_membership_type", membership);
  //   const code = membership === "member" ? "MM" : "DD";

  //   let queuePatientId = location.state?.patient_id || "";
  //   if (!queuePatientId) {
  //     try {
  //       const fromLocal = JSON.parse(localStorage.getItem("queue_id") || "null");
  //       queuePatientId = fromLocal?.patient_id || "";
  //     } catch (e) {
  //       queuePatientId = "";
  //     }
  //   }

  //   // Step 1: Check PhilHealth registration
  //   const philhealthRes = await checkPhilhealthIsRegistered(phicPin, code);
  //   const philhealthRegistered = philhealthRes && philhealthRes.status === 200 
  //     ? parseRegistrationResponse(philhealthRes) 
  //     : false;

  //   // Step 2: Check if patient is registered in PhilHealth Clinic
  //   const clinicRes = await checkPxIsPhilClinicRegistered(phicPin, code);
  //   const clinicRegistered = clinicRes && clinicRes.status === 200 
  //     ? parseRegistrationResponse(clinicRes) 
  //     : false;

  //   // Step 2.5: If clinic says NO, fetch PhilHealth registration details by PIN
  //   let philhealthRegRes = null;
  //   if (clinicRegistered === false) {
  //     philhealthRegRes = await getPhilhealthRegistrationByPin(phicPin, code, queuePatientId);
  //   }

  //   // Scenario 1: PhilHealth YES + Clinic YES → Patient exists, go directly to Vital Signs
  //   if (philhealthRegistered === true && clinicRegistered === true) {
  //     toast.success("Patient verified. Redirecting to Vital Signs...");
  //     const patientDetails = extractPatientDetails(clinicRes);
      
  //     // If patient_id exists, go directly to vital signs
  //     if (patientDetails.patient_id) {
  //       navigate("/patients/vitals/" + patientDetails.patient_id, {
  //         state: {
  //           patient_sex: patientDetails.sex,
  //           is_fpe: patientDetails.is_fpe,
  //           months: getMonths(patientDetails.birthdate),
  //           fromPhilhealthCheck: true,
  //         },
  //       });
  //     } else {
  //       // Fallback: if no patient_id, go to Add Patient form
  //       navigate("/patients/add/new-patient", {
  //         state: { 
  //           phic_pin: membership === "member" ? phicPin : "", 
  //           phic_dependent_pin: membership === "dependent" ? phicPin : "",
  //           phic_membership_type: membership,
  //           autofillData: patientDetails,
  //           // atc: atc, // Commented out - ATC not needed per client
  //         },
  //       });
  //     }
  //   }
  //   // Scenario 2: PhilHealth YES + Clinic NO → Go to Add Patient with autofill from PhilHealth
  //   else if (philhealthRegistered === true && clinicRegistered === false) {
  //     toast.info("Patient is registered to PhilHealth but not in Clinic.");
  //     const patientDetails = extractPatientDetails(philhealthRegRes ?? philhealthRes, true);
  //     navigate("/patients/add/new-patient", {
  //       state: {
  //         phic_pin: membership === "member" ? phicPin : "",
  //         phic_dependent_pin: membership === "dependent" ? phicPin : "",
  //         phic_membership_type: membership,
  //         autofillData: patientDetails,
  //         fromPhilhealthRegistration: true,
  //       },
  //     });
  //   }
  //   // Scenario 3: PhilHealth NO + Clinic YES → Go to Add Patient with autofill from clinic
  //   else if (philhealthRegistered === false && clinicRegistered === true) {
  //     toast.info("Patient is registered in Clinic but not in PhilHealth.");
  //     const patientDetails = extractPatientDetails(clinicRes);
  //     console.log('Navigating with autofillData:', patientDetails);
  //     navigate("/patients/add/new-patient", {
  //       state: { 
  //         phic_pin: membership === "member" ? phicPin : "", 
  //         phic_dependent_pin: membership === "dependent" ? phicPin : "",
  //         phic_membership_type: membership,
  //         autofillData: patientDetails,
  //         fromClinicRegistration: true,
  //       },
  //     });
  //   }
  //   // Scenario 4: PhilHealth NO + Clinic NO → Stay on screen, show error
  //   else {
  //     toast.error("Patient is NOT registered both in Philhealth and Clinic. Please check the PIN and try again.");
  //     setLoading(false);
  //     // Stay on IsRegisteredCheck screen to allow user to try again
  //     return;
  //   }

  //   setLoading(false);
  // };

  const handleSearch = async () => {
    setLoading(true);
    // Only set phic_pin in localStorage for members; for dependents, set phic_dependent_pin instead
    if (membership === "member") {
      localStorage.setItem("phic_pin", phicPin);
      localStorage.removeItem("phic_dependent_pin");
    } else {
      localStorage.setItem("phic_dependent_pin", phicPin);
      localStorage.removeItem("phic_pin");
    }
    localStorage.setItem("phic_membership_type", membership);
    const code = membership === "member" ? "MM" : "DD";

    let queuePatientId = location.state?.patient_id || "";
    if (!queuePatientId) {
      try {
        const fromLocal = JSON.parse(localStorage.getItem("queue_id") || "null");
        queuePatientId = fromLocal?.patient_id || "";
      } catch (e) {
        queuePatientId = "";
      }
    }

    // Step 1: Check PhilHealth registration
    const philhealthRes = await checkPhilhealthIsRegistered(phicPin, code);
    const philhealthRegistered = philhealthRes && philhealthRes.status === 200 
      ? parseRegistrationResponse(philhealthRes) 
      : false;

    // Step 2: Check if patient is registered in PhilHealth Clinic
    const clinicRes = await checkPxIsPhilClinicRegistered(phicPin, code);
    const clinicRegistered = clinicRes && clinicRes.status === 200 
      ? parseRegistrationResponse(clinicRes) 
      : false;

    // Step 2.5: Always fetch registration details by PIN so patient_data is available for autofill
    const philhealthRegRes = await getPhilhealthRegistrationByPin(phicPin, code, queuePatientId);

    // Bypass: always go to Add Patient after Search (Vital Signs redirect and stay-on-screen error commented out)
    const hasRegData =
      philhealthRegRes?.data?.patient_data || philhealthRegRes?.data?.data;
    const patientDetails = extractPatientDetails(
      hasRegData ? philhealthRegRes : clinicRes ?? philhealthRes,
      true
    );

    toast.info("Redirecting to Add Patient...");
    navigate("/patients/add/new-patient", {
      state: {
        phic_pin: membership === "member" ? phicPin : "",
        phic_dependent_pin: membership === "dependent" ? phicPin : "",
        phic_membership_type: membership,
        autofillData: patientDetails,
      },
    });

    // Scenario 1: PhilHealth YES + Clinic YES → Patient exists, go directly to Vital Signs
    // if (philhealthRegistered === true && clinicRegistered === true) {
    //   toast.success("Patient verified. Redirecting to Vital Signs...");
    //   const patientDetails = extractPatientDetails(clinicRes);
    //
    //   // If patient_id exists, go directly to vital signs
    //   if (patientDetails.patient_id) {
    //     navigate("/patients/vitals/" + patientDetails.patient_id, {
    //       state: {
    //         patient_sex: patientDetails.sex,
    //         is_fpe: patientDetails.is_fpe,
    //         months: getMonths(patientDetails.birthdate),
    //         fromPhilhealthCheck: true,
    //       },
    //     });
    //   } else {
    //     // Fallback: if no patient_id, go to Add Patient form
    //     navigate("/patients/add/new-patient", {
    //       state: {
    //         phic_pin: membership === "member" ? phicPin : "",
    //         phic_dependent_pin: membership === "dependent" ? phicPin : "",
    //         phic_membership_type: membership,
    //         autofillData: patientDetails,
    //         // atc: atc, // Commented out - ATC not needed per client
    //       },
    //     });
    //   }
    // }
    // // Scenario 2: PhilHealth YES + Clinic NO → Go to Add Patient with autofill from PhilHealth
    // else if (philhealthRegistered === true && clinicRegistered === false) {
    //   toast.info("Patient is registered to PhilHealth but not in Clinic.");
    //   const patientDetails = extractPatientDetails(philhealthRegRes ?? philhealthRes, true);
    //   navigate("/patients/add/new-patient", {
    //     state: {
    //       phic_pin: membership === "member" ? phicPin : "",
    //       phic_dependent_pin: membership === "dependent" ? phicPin : "",
    //       phic_membership_type: membership,
    //       autofillData: patientDetails,
    //       fromPhilhealthRegistration: true,
    //     },
    //   });
    // }
    // // Scenario 3: PhilHealth NO + Clinic YES → Go to Add Patient with autofill from clinic
    // else if (philhealthRegistered === false && clinicRegistered === true) {
    //   toast.info("Patient is registered in Clinic but not in PhilHealth.");
    //   const patientDetails = extractPatientDetails(clinicRes);
    //   console.log('Navigating with autofillData:', patientDetails);
    //   navigate("/patients/add/new-patient", {
    //     state: {
    //       phic_pin: membership === "member" ? phicPin : "",
    //       phic_dependent_pin: membership === "dependent" ? phicPin : "",
    //       phic_membership_type: membership,
    //       autofillData: patientDetails,
    //       fromClinicRegistration: true,
    //     },
    //   });
    // }
    // // Scenario 4: PhilHealth NO + Clinic NO → Stay on screen, show error
    // else {
    //   toast.error("Patient is NOT registered both in Philhealth and Clinic. Please check the PIN and try again.");
    //   setLoading(false);
    //   // Stay on IsRegisteredCheck screen to allow user to try again
    //   return;
    // }

    setLoading(false);
  };

  return (
    <div className="App">
      <Sidebar />
      <div className="add-customer col p-4 pt-4 overflow-scroll">
        <LabelHeader text={"PHILHEALTH REGISTRATION CHECK"} />
        <div className="pt-2 ml-7">
          <div className="row">
            <div className="col-sm-6">
              <TextField
                required
                id="phic_pin"
                name="phic_pin"
                formLabel={`PhilHealth Identification Number (PIN)`}
                size={`sm`}
                value={phicPin}
                onChange={(e) => setPhicPin(e.target.value)}
                maxLength={12}
              />
            </div>
            <div className="col-sm-6">
              <div className="row">
                <span>Membership</span>
                <Radio.Group
                  value={membership}
                  onChange={(e) => setMembership(e.target.value)}
                >
                  <Radio value="member">Member (MM)</Radio>
                  <Radio value="dependent">Dependent (DD)</Radio>
                </Radio.Group>
              </div>
            </div>
          </div>
          {/* ATC input field - Commented out per client request (ATC not needed) */}
          {/* <div className="row mt-3">
            <div className="col-sm-6">
              <TextField
                id="atc"
                name="atc"
                formLabel={`Authorization Transaction Code (ATC)`}
                size={`sm`}
                value={atc}
                onChange={(e) => setAtc(e.target.value)}
              />
            </div>
          </div> */}
          <div className="row justify-content-end mt-5 mb-3">
            <div className="col-2">
              <BtnSave
                label={"Search"}
                size={"sm"}
                onClick={handleSearch}
                isClicked={loading}
                disabled={!phicPin || phicPin.length !== 12 || !membership}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default IsRegisteredCheck;
